import {
  SzAccordion,
  SzBox,
  SzButton,
  SzCheckbox,
  SzIcon,
  SzModal,
  SzTypographie,
} from "@suezenv/react-theme-components";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import MapComponent from "../elements/mapComponent";
import InterventionService from "../../services/InterventionService";
import {FileDownloadStatus, IDocument} from "../../types";
import JSZip from "jszip";
import {store} from "../../store";
import {loadingSpinner} from "../../actions";
import {extractFileNameFromUrl} from "../../utils";
import {saveAs} from "file-saver";

type AccordionDocumentSetting = {
  key: string;
  types: string[];
};

export const BlockAssets = (props: any) => {
  const {t} = useTranslation();
  const {intervention} = props;
  const {documents} = intervention;
  const accordionDocumentSettings: AccordionDocumentSetting[] = [
    {
      key: 'archive',
      types: ['archive']
    },
    {
      key: 'autre',
      types: ['autre', 'en13508']
    },
    {
      key: 'document',
      types: ['document']
    },
    {
      key: 'image',
      types: ['image']
    },
    {
      key: 'video',
      types: ['video']
    },
  ];
  const [showModalVideo, setShowModalVideo] = useState(false);
  const [showModalProgressDownload, setShowModalProgressDownload] = useState(false);
  const [fileDownloadsProgress, setFileDownloadsProgress] = useState<Record<string, {
    status: FileDownloadStatus
  }>>({});
  const [checkboxesDownload, setCheckboxesDownload] = useState<Record<string, string>>({});
  const [activeKey, setActiveKey] = useState("Document");
  const [videoFile, setVideoFile] = useState({FilePath: "", FileName: ""});
  const onClick = (key: string): void => setActiveKey(key !== activeKey ? key : "");

  const updateFileProgress = (
    filename: string,
    status: FileDownloadStatus
  ) => {
    setFileDownloadsProgress((prev: any) => ({
      ...prev,
      [filename]: {
        ...prev[filename],
        status,
      },
    }));
  };

  const handleDownloadCheckboxChange = (file: IDocument): void => {
    setCheckboxesDownload((prev) => {
      const {Id: fileId, FilePath: filePath} = file;
      const next = {...prev};

      if (fileId in next) {
        delete next[fileId];
      } else {
        next[fileId] = filePath ?? '';
      }

      return next;
    });
  };

  /**
   * Fetches secure document URLs in chunks to respect API batch size limits
   */
  const fetchSecureDocumentsUrls = async (paths: string[], chunkSize: number = 5): Promise<string[]> => {
    let fileUrls: string[] = [];

    for (let i = 0; i < paths.length; i += chunkSize) {
      const batch = paths.slice(i, i + chunkSize);
      const {data: {urls}} = await InterventionService.secureDocumentUrls(batch);
      fileUrls.push(...urls);
    }

    return fileUrls;
  };

  const handleDownloadSelection = async (): Promise<void> => {
    store.dispatch(loadingSpinner(true));
    const fileUrls = await fetchSecureDocumentsUrls(Object.values(checkboxesDownload))
    fileUrls.map((url: string) => updateFileProgress(extractFileNameFromUrl(url), 'inprogress'));
    setShowModalProgressDownload(true);
    const zip = new JSZip();
    const tasks = fileUrls.map(async (url: string): Promise<void> => {
      const filename = extractFileNameFromUrl(url);

      try {
        const res = await fetch(url);

        if (!res.ok) {
          updateFileProgress(filename, 'error');

          return;
        }

        const blob = await res.blob();
        zip.file(filename, blob);
        updateFileProgress(filename, 'completed');
      } catch (err) {
        updateFileProgress(filename, 'error');
      }
    });

    await Promise.all(tasks);
    const zipBlob = await zip.generateAsync({type: 'blob'});
    saveAs(zipBlob, `itv-${intervention.Id}.zip`);
    store.dispatch(loadingSpinner(false));
  };

  const handleCloseModalVideo = (): void => {
    setShowModalVideo(!showModalVideo);
    setVideoFile({FilePath: "", FileName: ""});
  };

  const handleCloseModalProgressDownload = (): void => {
    setShowModalProgressDownload(!showModalProgressDownload);
    setFileDownloadsProgress({});
  };

  const header = (title: string): JSX.Element => (
    <SzTypographie weight="medium" className="mb-1">
      {title}
    </SzTypographie>
  );

  const renderDocumentDetail = (documentToDisplay: any): JSX.Element[] => {
    return documentToDisplay.map((file: any): JSX.Element => {
      const {Id: fileId, CodeTypeDocument: codeTypeDocument} = file;

      return (
        <div key={`document-${fileId}`} className="container row align-items-center m-0 p-0 pt-3">
          <div className={'col-1 pl-4'}>
            {codeTypeDocument !== 'Archive' && (
              <SzCheckbox
                label={''}
                name={`name-checkbox-${fileId}`}
                id={`id-checkbox-${fileId}`}
                onChange={(): void => handleDownloadCheckboxChange(file)}
                type="checkbox"
                checked={!!checkboxesDownload[fileId]}
              />
            )}
          </div>
          <div className={'col'}>
            <SzTypographie
              className="float-left mr-5 mt-3"
              color="inactive"
              weight="medium"
            >
              {file.FileName}
            </SzTypographie>
          </div>
          <div className={'col-3 d-flex justify-content-end'}>
            {!file.FileMimeType.toLowerCase().includes("video") ? (
              <SzIcon
                onClick={() => {
                  const link = document.createElement("a");
                  link.setAttribute("download", file.FileName);
                  InterventionService.secureDocumentUrls(
                    [file.FilePath],
                  ).then((response) => {
                    link.target = "blank";
                    link.href = response.data.urls[0];
                    link.click();
                  });
                }}
                className="cursor-pointer p-1 border"
                variant="line"
                icon="common-file-text-download"
              />
            ) : (
              <>
                <SzIcon
                  onClick={() => {
                    setVideoFile({
                      FileName: file.FileName,
                      FilePath: "",
                    });
                    InterventionService.secureDocumentUrls(
                      [file.FilePath],
                    ).then((response) => {
                      setVideoFile({
                        FileName: file.FileName,
                        FilePath: response.data.urls[0],
                      });
                    });
                    setShowModalVideo(true);
                  }}
                  className="cursor-pointer border p-1"
                  variant="line"
                  icon="arrow-button-right-2"
                />
                <SzIcon
                  onClick={() => {
                    const link = document.createElement("a");
                    link.setAttribute("download", file.FileName);
                    InterventionService.secureDocumentUrls(
                      [file.FilePath],
                    ).then((response) => {
                      link.target = "blank";
                      link.href = response.data.urls[0];
                      link.click();
                    });
                  }}
                  className=" ml-1 border cursor-pointer p-1"
                  variant="line"
                  icon="common-file-text-download"
                />
              </>
            )}
          </div>
        </div>
      );
    });
  };

  const renderDocument = (): JSX.Element => {
    return (
      <div className="document-list col-12">
        <SzTypographie color="inactive" className="mt-5" weight="bold">
          {t("intervention_detail.assets.document_title")}
        </SzTypographie>
        {Object.keys(checkboxesDownload).length > 0 && (
          <SzBox className="d-flex flex-column flex-sm-row gap-2 mb-3">
            <SzButton
              variant="primary"
              icon="zip-file-download"
              className="btn-outline-secondary border-0 px-1 m-1 w-100"
              onClick={(): void => {
                handleDownloadSelection();
              }}
            >
              {t("intervention_detail.assets.download_selection")}
            </SzButton>
            <SzButton
              variant="primary"
              icon="remove-square"
              className="btn-outline-secondary border-0 px-1 m-1 w-100"
              onClick={(): void => setCheckboxesDownload({})}
            >
              {t("intervention_detail.assets.empty_selection")}
            </SzButton>
          </SzBox>
        )}

        <SzAccordion activeKey={activeKey}>
          {accordionDocumentSettings.map((accordionDocumentSetting: AccordionDocumentSetting) => {
            const documentToDisplay: IDocument[] = documents.filter(
              (document: IDocument) => document.CodeTypeDocument
                && accordionDocumentSetting.types.includes(document.CodeTypeDocument.toLowerCase()),
            );
            if (documentToDisplay.length > 0) {
              return (
                <SzAccordion.SzCollapse
                  key={`accordion-${accordionDocumentSetting.key}`}
                  header={header(t(`intervention_detail.assets.${accordionDocumentSetting.key}`))}
                  body={renderDocumentDetail(documentToDisplay)}
                  eventKey={accordionDocumentSetting.key}
                  onClick={onClick}
                />
              );
            }
          })}
        </SzAccordion>
      </div>
    );
  };

  return (
    <>
      <SzModal
        title={t("intervention_detail.assets.download_modal.title")}
        show={showModalProgressDownload}
        size="lg"
        centered={true}
        handleClose={handleCloseModalProgressDownload}
      >
        <div className="text-left mt-5">
          {Object.entries(fileDownloadsProgress).map(([filename, data]) => {
            return (
              <SzTypographie weight="bold" className="d-flex">
                <span className="d-flex col">
                  {`"${filename}"`}
                </span>
                <span className="text-end">{t(`intervention_detail.assets.download_modal.${data.status}`)}</span>
              </SzTypographie>
            )
          })}
        </div>
      </SzModal>
      <SzModal
        className="mt-5 pt-5"
        title={videoFile.FileName}
        show={showModalVideo}
        centered={true}
        handleClose={handleCloseModalVideo}
        size={"lg"}
      >
        <div className="text-center mt-5">
          <video src={videoFile.FilePath} controls width="500"></video>
        </div>
      </SzModal>

      <SzBox
        className="row block-comment mr-0 ml-0 pb-0 pt-0 intervention-info-box items-bordered"
        tag="div"
      >
        <div className="col-4 pl-0">
          {documents.length > 0 && renderDocument()}
        </div>
        <div className="col-8">
          <MapComponent intervention={intervention}/>
        </div>
      </SzBox>
    </>
  );
};
